/**
 * Theme utility for injecting CSS variables from environment variables
 * This allows dynamic theming based on restaurant configuration
 */

/**
 * Converts a hex color to RGB values
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

/**
 * Lightens a color by a percentage
 */
function lightenColor(hex: string, percent: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const r = Math.min(255, Math.round(rgb.r + (255 - rgb.r) * percent));
  const g = Math.min(255, Math.round(rgb.g + (255 - rgb.g) * percent));
  const b = Math.min(255, Math.round(rgb.b + (255 - rgb.b) * percent));

  return `#${[r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Darkens a color by a percentage
 */
function darkenColor(hex: string, percent: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const r = Math.max(0, Math.round(rgb.r * (1 - percent)));
  const g = Math.max(0, Math.round(rgb.g * (1 - percent)));
  const b = Math.max(0, Math.round(rgb.b * (1 - percent)));

  return `#${[r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Injects CSS custom properties (variables) into the document root
 * This allows the entire app to use dynamic colors from environment variables
 */
export function injectThemeColors(): void {
  const root = document.documentElement;

  // Get colors from environment variables (set by build script)
  const primaryColor = import.meta.env.VITE_PRIMARY_COLOR || '#FF6600';
  const secondaryColor = import.meta.env.VITE_SECONDARY_COLOR || '#222222';

  // Generate derived colors
  const primaryHover = darkenColor(primaryColor, 0.1);
  const primaryLight = lightenColor(primaryColor, 0.2);
  const primaryLighter = lightenColor(primaryColor, 0.4);
  const primaryDark = darkenColor(primaryColor, 0.2);
  const primaryDarker = darkenColor(primaryColor, 0.3);

  const secondaryHover = lightenColor(secondaryColor, 0.1);
  const secondaryLight = lightenColor(secondaryColor, 0.3);
  const secondaryDark = darkenColor(secondaryColor, 0.2);

  // Set CSS custom properties
  root.style.setProperty('--color-primary', primaryColor);
  root.style.setProperty('--color-primary-hover', primaryHover);
  root.style.setProperty('--color-primary-light', primaryLight);
  root.style.setProperty('--color-primary-lighter', primaryLighter);
  root.style.setProperty('--color-primary-dark', primaryDark);
  root.style.setProperty('--color-primary-darker', primaryDarker);

  root.style.setProperty('--color-secondary', secondaryColor);
  root.style.setProperty('--color-secondary-hover', secondaryHover);
  root.style.setProperty('--color-secondary-light', secondaryLight);
  root.style.setProperty('--color-secondary-dark', secondaryDark);

  // Set RGB values for opacity variations
  const primaryRgb = hexToRgb(primaryColor);
  if (primaryRgb) {
    root.style.setProperty('--color-primary-rgb', `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}`);
  }

  const secondaryRgb = hexToRgb(secondaryColor);
  if (secondaryRgb) {
    root.style.setProperty('--color-secondary-rgb', `${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}`);
  }
}

/**
 * Get the current primary color from CSS variable
 */
export function getPrimaryColor(): string {
  return getComputedStyle(document.documentElement).getPropertyValue('--color-primary').trim() || '#FF6600';
}

/**
 * Get the current secondary color from CSS variable
 */
export function getSecondaryColor(): string {
  return getComputedStyle(document.documentElement).getPropertyValue('--color-secondary').trim() || '#222222';
}

